import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useEffect } from "react";
import { I18nProvider } from "@/i18n";
import { AuthProvider } from "@/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import InstallPrompt from "@/components/InstallPrompt";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import DashboardLayout from "@/pages/DashboardLayout";
import Dashboard from "@/pages/Dashboard";
import Vault from "@/pages/Vault";
import Messages from "@/pages/Messages";
import ProofOfLife from "@/pages/ProofOfLife";
import Heirs from "@/pages/Heirs";
import Capsule from "@/pages/Capsule";
import Historia from "@/pages/Historia";
import DigitalAssets from "@/pages/DigitalAssets";
import WillExecutor from "@/pages/WillExecutor";
import IceDocument from "@/pages/IceDocument";
import IceSharedView from "@/pages/IceSharedView";
import Admin from "@/pages/Admin";
import Settings from "@/pages/Settings";
import Plans from "@/pages/Plans";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PolConfirm from "@/pages/PolConfirm";
import ConfirmEmail from "@/pages/ConfirmEmail";
import AdminLogin from "@/pages/AdminLogin";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import FoundersProgram from "@/pages/FoundersProgram";
import { SHOW_FOUNDERS_PROGRAM } from "@/config/features";
import AvisoLegal from "@/pages/AvisoLegal";
import CookiesPolicy from "@/pages/Cookies";
import CookieBanner from "@/components/CookieBanner";
import PlanLimitProvider from "@/components/PlanLimitProvider";
import PaywallProvider from "@/components/PaywallProvider";
import ErrorBoundary from "@/components/ErrorBoundary";
import useKeyboardScroll from "@/hooks/useKeyboardScroll";
import "@/App.css";

function AppRouter() {
  const location = useLocation();
  // Process Emergent Auth callback BEFORE any route, prevents race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      {/* /register → unify into /login with the signup tab open */}
      <Route path="/register" element={<Navigate to="/login?signup=1" replace />} />
      <Route path="/signup" element={<Navigate to="/login?signup=1" replace />} />
      {/* Founders Program — public prospectus */}
      <Route
        path="/founders-program"
        element={SHOW_FOUNDERS_PROGRAM ? <FoundersProgram /> : <Navigate to="/plans" replace />}
      />
      <Route path="/programa-fundadores" element={<Navigate to="/founders-program" replace />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/pol-confirm/:token" element={<PolConfirm />} />
      <Route path="/confirm-email/:token" element={<ConfirmEmail />} />
      <Route path="/admin-login" element={<AdminLogin />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/legal/privacidad" element={<Privacy />} />
      <Route path="/legal/terminos" element={<Terms />} />
      <Route path="/legal/aviso-legal" element={<AvisoLegal />} />
      <Route path="/legal/cookies" element={<CookiesPolicy />} />
      {/* Public ICE share view — token + 6-digit code required. */}
      <Route path="/ice/share/:token" element={<IceSharedView />} />
      {/* /plans is public — Plans.jsx handles both authenticated and visitor states. */}
      <Route path="/plans" element={<Plans />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        }
      />
      <Route
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/vault" element={<Vault />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/proof-of-life" element={<ProofOfLife />} />
        <Route path="/heirs" element={<Heirs />} />
        <Route path="/digital-assets" element={<DigitalAssets />} />
        <Route path="/will-executor" element={<WillExecutor />} />
        <Route path="/ice" element={<IceDocument />} />
        <Route path="/capsule" element={<Capsule />} />
        <Route path="/historia" element={<Historia />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      {/* Catch-all: any unknown route lands on the marketing page (no
          black/empty screens for users who land on /register-old, etc.). */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      window.addEventListener("load", () => {
        navigator.serviceWorker.register("/sw.js").catch(() => {});
      });
    }
  }, []);

  return (
    <ErrorBoundary>
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <PaywallProvider>
              <AppRouter />
              <InstallPrompt />
              <CookieBanner />
              <PlanLimitProvider />
              <Toaster
                theme="dark"
                position="bottom-center"
                duration={2000}
                toastOptions={{
                  style: {
                    background: "rgba(30,29,27,0.95)",
                    border: "1px solid rgba(201,163,106,0.4)",
                    color: "#C9A36A",
                    fontFamily: "Manrope, system-ui, sans-serif",
                  },
                }}
              />
            </PaywallProvider>
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </ErrorBoundary>
  );
}

export default App;
